import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StageMoveDialog, type StageMoveRequest } from "@/components/deals/StageMoveDialog";
import { CheckCircle2, Circle, Clock, FileCheck2, Flag, Loader2, Paperclip, Target, ArrowRight } from "lucide-react";
import type { MasterLookupRecord } from "@/types";

type GateItem = { key: string; label: string; met: boolean; hint?: string };
type Milestone = { id: string; key: string; label: string; source: string; note: string | null; file_name: string | null; has_file: boolean; created_on: string };
type Qualification = {
  budget_amount: string | number | null;
  budget_confirmed: boolean;
  decision_maker_contact_id: string | null;
  authority_notes: string | null;
  need: string | null;
  timeline_date: string | null;
  timeline_notes: string | null;
  competitors: string | null;
  pain_points: string | null;
  product_type: string | null;
  required_qty: string | number | null;
  qty_unit: string | null;
  target_price: string | number | null;
  end_use: string | null;
  required_by: string | null;
};
type Gates = {
  stage: { id: string; name: string; key: string | null; sla_days: number | null } | null;
  next_stage: { id: string; name: string; key: string } | null;
  gated: boolean;
  checklist: GateItem[];
  ready: boolean;
  days_in_stage: number;
  over_time_limit: boolean;
  qualification: Qualification | null;
  milestones: Milestone[];
};

const EMPTY_REQ = { product_type: "", need: "", required_qty: "", qty_unit: "", target_price: "", end_use: "", required_by: "", competitors: "" };

type Recordable = { key: string; button: string; title: string; description: string; notePlaceholder: string; needsFile: boolean };

/** Milestones a rep records by hand: the button text, dialog wording and whether a file is needed */
const SAMPLE_APPROVED: Recordable = {
  key: "SAMPLE_APPROVED",
  button: "Buyer approved the sample (record)",
  title: "Buyer approved the sample",
  description: "For an approval received outside the CRM (call, WhatsApp, email). The deal then moves to Quotation.",
  notePlaceholder: "e.g. Buyer approved the navy lab dip on WhatsApp, 12 Oct",
  needsFile: false,
};
const SAMPLE_NOT_REQUIRED: Recordable = {
  key: "SAMPLE_NOT_REQUIRED",
  button: "Sampling not required",
  title: "Sampling not required",
  description: "Use this when the buyer needs no sample, for example a repeat order of an approved article. The deal then moves to Quotation.",
  notePlaceholder: "e.g. Repeat buyer, same article as last season",
  needsFile: false,
};
const TERMS_AGREED: Recordable = {
  key: "TERMS_AGREED",
  button: "Price and terms agreed (record)",
  title: "Price and terms agreed",
  description: "Record the final price, credit and delivery terms the buyer agreed. The deal then moves to Order Confirmed.",
  notePlaceholder: "e.g. Agreed Rs 138/m, 45 days credit, ex-mill, on call 12 Oct",
  needsFile: false,
};
const PO_RECEIVED: Recordable = {
  key: "PO_RECEIVED",
  button: "Upload purchase order",
  title: "Purchase order received",
  description: "Attach the buyer's purchase order. The deal is then closed as Won.",
  notePlaceholder: "e.g. PO 4471 received by email on 14 Oct",
  needsFile: true,
};

/**
 * The deal's place in the textile pipeline: what this stage needs before the deal can move on (the enquiry
 * requirement, then sample approval, quote acceptance, agreed terms and the buyer's PO), how long it has been
 * here, and the milestones so far.
 */
export function DealStageCard({
  dealId,
  dealName,
  accountId,
  isClosed,
  onChanged,
}: {
  dealId: string;
  dealName: string;
  accountId?: string;
  isClosed: boolean;
  onChanged: () => void;
}) {
  const { success, error } = useToast();
  const [gates, setGates] = useState<Gates | null>(null);
  const [req, setReq] = useState(EMPTY_REQ);
  const [productTypes, setProductTypes] = useState<MasterLookupRecord[]>([]);
  const [units, setUnits] = useState<MasterLookupRecord[]>([]);
  const [endUses, setEndUses] = useState<MasterLookupRecord[]>([]);
  const [saving, setSaving] = useState(false);
  const [moveRequest, setMoveRequest] = useState<StageMoveRequest>(null);
  const [recordOpen, setRecordOpen] = useState<null | Recordable>(null);
  const [recordNote, setRecordNote] = useState("");
  const [recordFile, setRecordFile] = useState<File | null>(null);

  const load = async () => {
    try {
      const g: any = await api.get(`/api/Deal/Gates/${dealId}`);
      setGates(g);
      const q = g?.qualification;
      setReq({
        product_type: q?.product_type || "",
        need: q?.need || "",
        required_qty: q?.required_qty != null ? String(Number(q.required_qty)) : "",
        qty_unit: q?.qty_unit || "",
        target_price: q?.target_price != null ? String(Number(q.target_price)) : "",
        end_use: q?.end_use || "",
        required_by: q?.required_by ? String(q.required_by).slice(0, 10) : "",
        competitors: q?.competitors || "",
      });
    } catch (err: any) {
      error("Could not load the stage requirements", err.message || "");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Pick-lists for the enquiry requirement
  useEffect(() => {
    api.get("/api/MasterLookup/ByCategory/PRODUCT_TYPE").then((r) => setProductTypes(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
    api.get("/api/MasterLookup/ByCategory/PRODUCT_UNIT").then((r) => setUnits(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
    api.get("/api/MasterLookup/ByCategory/END_USE").then((r) => setEndUses(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
  }, []);

  const saveRequirement = async () => {
    setSaving(true);
    try {
      const body = {
        product_type: req.product_type || null,
        need: req.need || null,
        required_qty: req.required_qty === "" ? null : Number(req.required_qty),
        qty_unit: req.qty_unit || null,
        target_price: req.target_price === "" ? null : Number(req.target_price),
        end_use: req.end_use || null,
        required_by: req.required_by || null,
        competitors: req.competitors || null,
      };
      const g: any = await api.put(`/api/Deal/Qualification/${dealId}`, body);
      setGates(g);
      success("Requirement saved", g?.ready ? "Ready to move to the next stage" : "");
      onChanged();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const recordMilestone = async () => {
    if (!recordOpen) return;
    setSaving(true);
    try {
      let file_url: string | undefined;
      let file_name: string | undefined;
      if (recordFile) {
        const fd = new FormData();
        fd.append("deal_id", dealId);
        fd.append("file", recordFile);
        const up: any = await api.upload("/api/Upload/File", fd);
        file_url = up?.file_url;
        file_name = recordFile.name;
      }
      const res: any = await api.post(`/api/Deal/Milestone/${dealId}`, {
        milestone_key: recordOpen.key,
        note: recordNote.trim(),
        file_url,
        file_name,
      });
      success("Recorded", res?.advanced_to ? `The deal moved to ${res.advanced_to}` : "Milestone saved");
      setRecordOpen(null);
      setRecordNote("");
      setRecordFile(null);
      await load();
      onChanged();
    } catch (err: any) {
      error("Could not record it", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const downloadMilestoneFile = async (m: Milestone) => {
    try {
      const res = await fetch(`/api/Deal/MilestoneFile/${m.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      error("Could not open the file", err.message || "");
    }
  };

  if (!gates) return null;
  const key = gates.stage?.key;
  if (!gates.gated) return null;
  const done = gates.checklist.filter((i) => i.met).length;
  const field = "w-full h-10 sm:h-11 rounded-lg border border-input bg-background px-3.5 text-sm sm:text-[15px] focus:outline-hidden focus:ring-2 focus:ring-ring";

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <Target className="size-4 text-primary" /> {gates.stage?.name}
            {gates.checklist.length > 0 && (
              <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${gates.ready ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}>
                {gates.ready ? "Ready" : `${done}/${gates.checklist.length} done`}
              </span>
            )}
          </p>
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${gates.over_time_limit ? "text-red-600 dark:text-red-400 font-medium" : "text-muted-foreground"}`}>
            <Clock className="size-3" />
            {gates.days_in_stage} day{gates.days_in_stage === 1 ? "" : "s"} in this stage
            {gates.stage?.sla_days ? ` (limit ${gates.stage.sla_days})` : ""}
            {gates.over_time_limit ? " · over the limit" : ""}
          </p>
        </div>
        {!isClosed && gates.next_stage && key !== "ORDER" && (
          <Button
            size="sm"
            variant={gates.ready ? "default" : "outline"}
            className="h-8 text-xs gap-1"
            onClick={() => setMoveRequest({ dealId, dealName, stage: { id: gates.next_stage!.id, name: gates.next_stage!.name } })}
          >
            Move to {gates.next_stage.name} <ArrowRight className="size-3.5" />
          </Button>
        )}
      </div>

      {!isClosed && gates.checklist.length > 0 && (
        <ul className="space-y-1">
          {gates.checklist.map((i) => (
            <li key={i.key} className="flex items-start gap-2 text-xs">
              {i.met ? <CheckCircle2 className="size-4 text-emerald-600 shrink-0" /> : <Circle className="size-4 text-muted-foreground shrink-0" />}
              <span>
                <span className={i.met ? "text-foreground" : "text-foreground font-medium"}>{i.label}</span>
                {!i.met && i.hint && <span className="block text-muted-foreground">{i.hint}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!isClosed && key === "ENQUIRY" && (
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2.5">
          <p className="text-xs font-semibold">What the buyer wants</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="space-y-1">
              <Label className="text-xs">Product type *</Label>
              <select value={req.product_type} onChange={(e) => setReq({ ...req, product_type: e.target.value })} className={field}>
                <option value="">— Choose —</option>
                {productTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End use</Label>
              <select value={req.end_use} onChange={(e) => setReq({ ...req, end_use: e.target.value })} className={field}>
                <option value="">— Choose —</option>
                {endUses.map((u) => (
                  <option key={u.code} value={u.code}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Specification * — quality, count / GSM, width, shade, finish, packing</Label>
              <Textarea rows={2} value={req.need} onChange={(e) => setReq({ ...req, need: e.target.value })} placeholder="e.g. 40s cotton poplin, 110 GSM, 58 inch, navy blue, soft finish, rolls of 100 m" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quantity *</Label>
              <div className="flex gap-2">
                <Input type="number" min="0" step="any" value={req.required_qty} onChange={(e) => setReq({ ...req, required_qty: e.target.value })} className="h-9" />
                <select value={req.qty_unit} onChange={(e) => setReq({ ...req, qty_unit: e.target.value })} className="h-9 w-28 shrink-0 rounded-lg border border-input bg-background px-2 text-sm">
                  <option value="">Unit</option>
                  {units.map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Target price / unit (₹)</Label>
              <Input type="number" min="0" step="any" value={req.target_price} onChange={(e) => setReq({ ...req, target_price: e.target.value })} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Required by *</Label>
              <Input type="date" value={req.required_by} onChange={(e) => setReq({ ...req, required_by: e.target.value })} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Competing suppliers (optional)</Label>
              <Input value={req.competitors} onChange={(e) => setReq({ ...req, competitors: e.target.value })} className="h-9" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={saveRequirement} disabled={saving} className="h-8 text-xs">
              {saving ? <Loader2 className="size-3.5 animate-spin mr-1" /> : null}
              Save requirement
            </Button>
          </div>
        </div>
      )}

      {!isClosed && key === "SAMPLING" && !gates.ready && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setRecordOpen(SAMPLE_APPROVED)}>
            <Flag className="size-3.5" /> {SAMPLE_APPROVED.button}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setRecordOpen(SAMPLE_NOT_REQUIRED)}>
            <Flag className="size-3.5" /> {SAMPLE_NOT_REQUIRED.button}
          </Button>
        </div>
      )}
      {!isClosed && key === "QUOTATION" && !gates.ready && (
        <p className="text-xs text-muted-foreground">When the buyer accepts, mark the quotation as accepted in Quotations below; the deal then moves to Negotiation.</p>
      )}
      {!isClosed && key === "NEGOTIATION" && !gates.ready && (
        <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setRecordOpen(TERMS_AGREED)}>
          <Flag className="size-3.5" /> {TERMS_AGREED.button}
        </Button>
      )}
      {!isClosed && key === "ORDER" && !gates.ready && (
        <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setRecordOpen(PO_RECEIVED)}>
          <FileCheck2 className="size-3.5" /> {PO_RECEIVED.button}
        </Button>
      )}

      {gates.milestones.length > 0 && (
        <div className="border-t border-border pt-2.5 space-y-1.5">
          <p className="text-xs font-semibold">Milestones</p>
          {gates.milestones.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-foreground">
                  {m.label}
                  <span className="text-muted-foreground"> · {new Date(m.created_on).toLocaleDateString()}{m.source === "MANUAL" ? " · recorded by hand" : ""}</span>
                </p>
                {m.note && <p className="text-muted-foreground break-words">{m.note}</p>}
                {m.has_file && (
                  <button type="button" onClick={() => downloadMilestoneFile(m)} className="inline-flex items-center gap-1 text-primary hover:underline">
                    <Paperclip className="size-3" /> {m.file_name || "Attachment"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <StageMoveDialog
        request={moveRequest}
        onClose={() => setMoveRequest(null)}
        onMoved={() => {
          load();
          onChanged();
        }}
      />

      <Dialog open={!!recordOpen} onOpenChange={(o) => !o && !saving && setRecordOpen(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{recordOpen?.title}</DialogTitle>
            <DialogDescription>{recordOpen?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>How and when did the buyer confirm? *</Label>
              <Textarea rows={3} value={recordNote} onChange={(e) => setRecordNote(e.target.value)} placeholder={recordOpen?.notePlaceholder} />
            </div>
            {recordOpen?.needsFile && (
              <div className="space-y-2">
                <Label>File *</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setRecordFile(e.target.files?.[0] || null)} />
                <p className="text-xs text-muted-foreground">Closing as Won uses the deal amount (set from the accepted quotation).</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={recordMilestone} disabled={saving || !recordNote.trim() || (!!recordOpen?.needsFile && !recordFile)}>
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
